import Konva from "konva";
import type { PreViewBarOption } from "../index";
import type { UiTooltipFunction } from "various-ui";

import { disposeData } from "./dispost";

export const useComposable = () => {
    //* 获取i18n
    const i18n = useI18n();

    //* 响应式变量
    const refs = {
        mode: ref<string>("overview"),

        mainNode: ref<HTMLDivElement>(),
        tooltipNode: ref<InstanceType<typeof UiTooltipFunction>>(),

        mrna: ref<Konva.Group>(),
        main: ref<Konva.Layer>(),
        stage: ref<Konva.Stage>(),
        mutation: ref<Konva.Group>(),

        info: ref<any>(),
        offset: ref<number>(0),
        content: ref<any>(),
        drawing: ref<any>(),
    };

    //* Bar配置项
    const bar = reactive<PreViewBarOption>({ el: undefined, size: 0, ratio: 0 });

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: function (data: any) {
            refs.drawing.value = new disposeData(data, { width: refs.mainNode.value?.clientWidth || 0 });
            methods.draw("overview");
        },

        //* 绘制函数
        draw: function (mode: string) {
            if (!refs.mainNode.value) return;

            //- 1. 初始化舞台与场景
            if (refs.stage.value) {
                refs.offset.value = 0;
                refs.main.value?.destroyChildren();
            } else {
                refs.stage.value = new Konva.Stage({ container: refs.mainNode.value, width: refs.drawing.value.view.width, height: 131 });
                refs.main.value = new Konva.Layer();
            }

            if (!refs.main.value || !refs.stage.value) return;

            //- 2. 根据绘制的模式进行不同的数据处理
            switch (mode) {
                case "overview":
                    refs.mode.value = "overview";
                    refs.drawing.value.disposeOverviewData();
                    break;
                case "details":
                    refs.mode.value = "details";
                    refs.drawing.value.disposeDetailsData();
                    break;
            }

            //- 3. 绘制Mrna
            refs.mrna.value = new Konva.Group();
            refs.content.value = new Konva.Group();
            refs.drawing.value.dictionaries.forEach((value: any) => {
                switch (value.type) {
                    case "intron":
                        return methods.drawIntron(value);
                    case "exon":
                        return methods.drawExon(value);
                }
            });

            //- 4. 绘制滚动条
            methods.drawScrollBar();

            //- 5. 初始化突变信息
            refs.mutation.value = new Konva.Group();

            //- 将场景添加入舞台中
            refs.mrna.value.add(refs.mutation.value);
            refs.mrna.value.add(refs.content.value);
            refs.main.value.add(refs.mrna.value);
            refs.stage.value.add(refs.main.value);
        },

        //* 绘制Exon
        drawExon: function (value: any) {
            //- 1. 生成容器
            const container = new Konva.Group();

            //- 2. 绘制线框
            container.add(
                new Konva.Rect({
                    x: value.offset + 0.5,
                    y: 60.5,
                    width: value.width - 1,
                    height: 23,
                    stroke: "#999999",
                    strokeWidth: 1,
                    cornerRadius: 2,
                })
            );

            //- 3. 绘制CDS区域
            if (value.cds) {
                //- 2.1 初始化数据
                const end = refs.drawing.value.disposePos(value.cds.end, { align: "right" });
                const start = refs.drawing.value.disposePos(value.cds.start);
                const radius = [0, 0, 0, 0];
                //- 2.2 判断CDS左侧是否需要圆角
                if (value.cds.start == value.start) {
                    radius[0] = 2;
                    radius[3] = 2;
                }
                //- 2.2 判断CDS右侧是否需要圆角
                if (value.cds.end == value.end) {
                    radius[1] = 2;
                    radius[2] = 2;
                }
                //- 2.3 绘制
                container.add(
                    new Konva.Rect({ x: start.offset, y: 60, width: end.offset - start.offset, height: 24, fill: "#00C7C8", cornerRadius: radius })
                );
            }

            //- 4. 绘制文本
            const content = new Konva.Text({
                x: value.offset + value.width / 2,
                y: 66,
                fontFamily: "Noto Sans CJK Regular",
                fontSize: 14,
                fill: "#4a4a4a",
                text: value.name,
            });

            refs.content.value.add(content.offsetX(content.getTextWidth() / 2));

            container.on("mouseenter", (ev) => {
                refs.info.value = value;
                refs.tooltipNode.value?.show("start", {
                    pageX: ev.evt.x,
                    pageY: ev.evt.y,
                    initialization: true
                });
            });

            container.on("mousemove", (ev) => {
                refs.tooltipNode.value?.show("start", {
                    pageX: ev.evt.x,
                    pageY: ev.evt.y,
                    initialization: true
                });
            });

            container.on("mouseleave", (ev) => {
                refs.tooltipNode.value?.hidden();
            });

            //- 6. 将容器添加进场景中
            refs.mrna.value?.add(container);
        },

        //* 绘制Intron
        drawIntron: function (value: any) {
            if (value.omit) {
                refs.mrna.value?.add(new Konva.Rect({ x: value.offset, y: 71, width: 25, height: 2, fill: "#4a4a4a" }));
                refs.mrna.value?.add(new Konva.Rect({ x: value.offset + 31, y: 71, width: 2, height: 2, fill: "#4a4a4a", rotation: 2 }));
                refs.mrna.value?.add(new Konva.Rect({ x: value.offset + 39, y: 71, width: 2, height: 2, fill: "#4a4a4a", rotation: 2 }));
                refs.mrna.value?.add(new Konva.Rect({ x: value.offset + 47, y: 71, width: 2, height: 2, fill: "#4a4a4a", rotation: 2 }));
                refs.mrna.value?.add(new Konva.Rect({ x: value.offset + 55, y: 71, width: 25, height: 2, fill: "#4a4a4a" }));
            } else {
                refs.mrna.value?.add(new Konva.Rect({ x: value.offset, y: 71, width: value.width, height: 2, fill: "#4a4a4a" }));
            }
        },

        //* 绘制滚动条
        drawScrollBar: function () {
            if (refs.drawing.value.size > refs.drawing.value.view.width) {
                //- 1. 计算Bar尺寸
                bar.ratio = (refs.drawing.value.view.width - 2) / refs.drawing.value.size;
                bar.size = bar.ratio * refs.drawing.value.view.width;

                //- 2. 绘制滚动条背景框
                const wireframe = new Konva.Rect({ x: 0, y: 112, width: refs.drawing.value.view.width, height: 16, fill: "#F4F4F4" });

                //- 3. 绘制Bar
                bar.el = new Konva.Rect({
                    x: 1,
                    y: 110,
                    width: bar.size,
                    height: 20,
                    fill: "#fff",
                    stroke: "#999999",
                    strokeWidth: 1,
                    fillAfterStrokeEnabled: true,
                });

                //- 4. 悬浮鼠标样式修改
                bar.el.on("mouseenter", (ev: any) => {
                    ev.evt.target.style.cursor = "pointer";
                    bar.el?.stroke("#666666");
                });
                bar.el.on("mouseleave", (ev: any) => {
                    ev.evt.target.style.cursor = "auto";
                    bar.el?.stroke("#999999");
                });

                //- 5. Bar拖动反馈
                bar.el.on("mousedown", (ev: any) => {
                    const clientX = ev.evt.clientX;
                    const offsetX = refs.offset.value;
                    //- 鼠标移动反馈事件, 边界检测
                    document.onmousemove = (ev) => {
                        //- 偏移记录
                        refs.offset.value = methods.BoundaryDetection(offsetX + ev.clientX - clientX);
                        //- 触发移动反馈事件
                        bar.el?.offsetX(-refs.offset.value);
                        refs.mrna.value?.offsetX(refs.offset.value / bar.ratio);
                    };
                    //- 鼠标松开反馈事件, 左右端20%的区域触发Mrna偏移, 偏移边界检测
                    document.onmouseup = () => {
                        //- 清空事件
                        document.onmouseup = null;
                        document.onmousemove = null;
                    };
                });

                refs.main.value?.add(wireframe);
                refs.main.value?.add(bar.el as Konva.Rect);
            } else {
                bar.el = undefined;
                bar.size = 0;
                bar.ratio = 0;
            }
        },

        //* 绘制预测点
        drawPos: function (data: any) {
            //- 1. 检测是否为详情图
            if (refs.mode.value == "overview") {
                methods.draw("details");
            }

            //- 2. 旧数据清除与添加
            refs.mutation.value?.destroyChildren();

            //- 3. 获取start与end的位置
            const end = refs.drawing.value.disposePos(data.end, { align: "right" });
            const start = refs.drawing.value.disposePos(data.start);

            //- 4. 存在Bar时才进行偏移定位
            if (bar.el) {
                let offset = 0;
                if (start.offset < refs.drawing.value.view.width / 2) {
                    refs.offset.value = 0;
                } else if (start.offset > refs.drawing.value.size - refs.drawing.value.view.width / 2) {
                    offset = refs.drawing.value.size - refs.drawing.value.view.width;
                    refs.offset.value = offset * bar.ratio;
                } else {
                    offset = start.offset - refs.drawing.value.view.width / 2;
                    refs.offset.value = offset * bar.ratio;
                }

                bar.el.offsetX(-refs.offset.value);
                refs.mrna.value?.offsetX(offset);
            }

            //- 5. 绘制预测点位
            //- 5.1 数据初始化
            const ref = data.ref ? (data.ref.length > 10 ? `${data.ref.slice(0, 3)}···${data.ref.slice(-3)}` : data.ref) : "--";
            const alt = data.alt ? (data.alt.length > 10 ? `${data.alt.slice(0, 3)}···${data.alt.slice(-3)}` : data.alt) : "del";
            const config = {
                content: [`${i18n.t("BioTools.Mutation")}: ${ref}${alt == "del" ? "" : ` > ${alt}`}`],
                offset: start.offset + (end.offset - start.offset) / 2,
                size: 0,
            };
            if (ref.length > 10 || alt.length > 10) {
                if (alt === "del") {
                    config.content.push(`${ref.length}bp`);
                } else {
                    config.content.push(` (${ref.length}bp > ${alt.length}bp)`);
                }
            }

            //- 5.2 绘制线条区域
            refs.mutation.value?.add(
                new Konva.Rect({ x: start.offset, y: 32, width: 1, height: start.data.type == "intron" ? 40 : 28, fill: "#ce2424" })
            );
            refs.mutation.value?.add(
                new Konva.Rect({ x: end.offset - 1, y: 32, width: 1, height: end.data.type == "intron" ? 40 : 28, fill: "#ce2424" })
            );

            //- 5.3 初始化文本
            const mutation = new Konva.Text({
                y: 0,
                height: 32,
                fontFamily: "Noto Sans CJK Regular",
                fontSize: 14,
                fill: "#ffffff",
                align: "center",
                verticalAlign: "middle",
                text: config.content.join(""),
            });

            //- 5.4 计算文本偏移位置
            config.size = mutation.getTextWidth() + 32;
            if (config.size < end.offset - start.offset + 16) {
                config.size = end.offset - start.offset + 16;
            }

            //- 5.5 处理边界问题并绘制突变信息
            mutation.width(config.size);
            if (config.offset - config.size / 2 < 0) {
                refs.mutation.value?.add(new Konva.Rect({ x: 0, y: 0, width: config.size, height: 32, fill: "#ce2424", cornerRadius: 2 }));
                refs.mutation.value?.add(mutation.x(0));
            } else if (config.offset + config.size > refs.drawing.value.size) {
                const offsetX = refs.drawing.value.size - config.size;
                refs.mutation.value?.add(new Konva.Rect({ x: offsetX, y: 0, width: config.size, height: 32, fill: "#ce2424", cornerRadius: 2 }));
                refs.mutation.value?.add(mutation.x(offsetX));
            } else {
                const offsetX = config.offset - config.size / 2;
                refs.mutation.value?.add(new Konva.Rect({ x: offsetX, y: 0, width: config.size, height: 32, fill: "#ce2424", cornerRadius: 2 }));
                refs.mutation.value?.add(mutation.x(offsetX));
            }

            //- 6. 计算预测点位经过的Exon并绘制预测区域
            refs.drawing.value.dictionaries.map((value: any) => {
                if (value.type == "exon") {
                    if (data.start <= value.end && data.start >= value.start) {
                        if (data.end <= value.end && data.end >= value.start) {
                            const start = refs.drawing.value.disposePos(data.start);
                            const end = refs.drawing.value.disposePos(data.end, { align: "right" });
                            refs.mutation.value?.add(
                                new Konva.Rect({ x: start.offset, y: 60, width: end.offset - start.offset, height: 24, fill: "#FFD40B" })
                            );
                        } else {
                            const start = (data.start - value.start) * value.ratio;
                            refs.mutation.value?.add(
                                new Konva.Rect({
                                    x: value.offset + start,
                                    y: 60,
                                    width: value.width - start,
                                    height: 24,
                                    cornerRadius: [0, 2, 2, 0],
                                    fill: "#FFD40B",
                                })
                            );
                        }
                    }

                    if (data.end <= value.end && data.end >= value.start && data.start <= value.start) {
                        refs.mutation.value?.add(
                            new Konva.Rect({
                                x: value.offset,
                                y: 60,
                                width: (data.end - value.start + 1) * value.ratio,
                                height: 24,
                                cornerRadius: [2, 0, 0, 2],
                                fill: "#FFD40B",
                            })
                        );
                    }

                    if (data.start <= value.start && data.end >= value.end) {
                        refs.mutation.value?.add(
                            new Konva.Rect({ x: value.offset, y: 60, width: value.width, height: 24, cornerRadius: 2, fill: "#FFD40B" })
                        );
                    }
                }
            });

            refs.mutation.value?.on("mouseenter", (ev: any) => {
                refs.info.value = data;
                refs.tooltipNode.value?.show("start", {
                    pageX: ev.evt.x,
                    pageY: ev.evt.y,
                    initialization: true
                });
            });

            refs.mutation.value?.on("mousemove", (ev: any) => {
                refs.tooltipNode.value?.show("start", {
                    pageX: ev.evt.x,
                    pageY: ev.evt.y,
                    initialization: true
                });
            });

            refs.mutation.value?.on("mouseleave", (ev: any) => {
                refs.tooltipNode.value?.hidden();
            });
        },

        //* 边界检测函数
        BoundaryDetection: function (offset: number): number {
            //- 1. 检测左侧贴边
            //- 1.1. 当mrna未进行偏移时最小值为0
            if (offset < 0) {
                return 0;
            } else if (offset + bar.size > refs.drawing.value.view.width - 2) {
                return refs.drawing.value.view.width - bar.size - 2;
            }

            //- 偏移记录
            return offset;
        },
    };

    return { refs, methods };
};
